import React from "react";
import { createInertiaApp, router } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import Honeybadger from "@honeybadger-io/js";
import { HoneybadgerErrorBoundary } from "@honeybadger-io/react";
import CustomErrorBoundaryComponent from "./custom_error_boundary";
import "../utils/axios_setup";
import { initMobiLoudPush } from "../mobiloud";
import {
  initCapacitorPush,
  registerBackButton,
  initNativeUi,
  initKeyboard,
  initOfflineWatch,
  registerResumeRefresh,
  registerCableReconnect,
  registerPullToRefresh,
  registerDeepLinks,
  registerDeviceHealthSync,
  loadNativeAppInfo,
  appShellErrorContext,
  appShellErrorTags,
} from "../capacitor";
import { startOfflineSync } from "../offline";
import { initializeMultiAccount } from "../utils/multi_account_init";
import { initAppNotificationUrlParamHook } from "../utils/app_notification_url_param";
import { startLockTelemetryPageView } from "../utils/lock_telemetry";
import { cacheBustingReload } from "../utils/cache_busting_reload";
import { isAccountChangedNoise } from "../utils/account_changed_noise";

document.addEventListener("DOMContentLoaded", () => {
  // Initialize native push notifications. Each no-ops outside its own shell, so
  // both can be called during the MobiLoud → Capacitor cutover.
  initMobiLoudPush();
  initCapacitorPush();
  // Route Android's back button to in-app history (no-op off Android/Capacitor).
  registerBackButton();
  // Native keyboard UX: keyboard-open class + keep chat pinned to newest
  // message (no-op off the Capacitor shell).
  initKeyboard();
  // Show a native offline banner when connectivity drops (no-op off-shell).
  initOfflineWatch();
  // Offline workout logging: queue set saves and form videos made with no
  // signal and sync them on reconnect (no-op unless offline_workout_logging).
  startOfflineSync();
  // Refresh the web app when reopened after a long background (no-op off-shell).
  registerResumeRefresh();
  // Reopen the ActionCable socket on foreground so chat/badges resume live
  // updates after the WebView suspended it (no-op off the Capacitor shell).
  registerCableReconnect();
  // Pull-to-refresh the current page from the top of the main scroller
  // (no-op off the Capacitor shell).
  registerPullToRefresh();
  // Route incoming Universal/App Links to the in-app router (no-op off-shell).
  registerDeepLinks();
  // Apple Health / Health Connect: sync a connected client's daily data on
  // launch and foreground; never prompts (no-op off-shell, without the native
  // plugin, or without device_health_ingestion).
  registerDeviceHealthSync();
  // Cache the native app version/build for Honeybadger tags (no-op off-shell).
  loadNativeAppInfo();

  // Initialize multi-account support (adds current session to localStorage if enabled)
  initializeMultiAccount();

  // Suppress benign ResizeObserver errors from browser console
  // These occur frequently with Material UI and don't indicate real problems
  window.addEventListener("error", (event) => {
    if (event.message?.includes("ResizeObserver loop")) {
      event.stopImmediatePropagation();
    }
  });

  const honeybadgerEnvironment = window.aws_env?.trim() || window.environment;

  const honeybadger = Honeybadger.configure({
    apiKey: "bcc95794",
    environment: honeybadgerEnvironment,
    // Uncomment to enable reporting in development
    // reportData: true,
  });

  if (window._tkcCurrentUserId) {
    honeybadger.setContext({
      user_id: window._tkcCurrentUserId,
      user_email: window.bloc_user_email,
      // Attribution for churn analysis (guarded for nil — a client with no
      // company or no active primary coach).
      company_id: window._tkcCompanyId ?? undefined,
      coach_id: window._tkcPrimaryCoachId ?? undefined,
    });
  }

  const ignorePatterns = [
    /This video will be available for viewing shortly/i,
    /ResizeObserver loop limit exceeded/i,
    /ResizeObserver loop completed with undelivered notifications/i,
    // 402 Payment Required is an expected business state, not a bug: see
    // VerifyPlanFeature / Api::RestApiController#verify_company_billing_status
    // on the Rails side. utils/axios_setup.js now turns every one of these
    // into a graceful snackbar instead of letting it reach here as noise
    // (HB #130636645). Scoped to exactly this message so other 4xx/5xx axios
    // failures are still reported normally.
    /^Request failed with status code 402$/,
  ];

  Honeybadger.beforeNotify(function (notice) {
    if (ignorePatterns.some((p) => p.test(notice.message))) {
      return false;
    }

    // A stale page's 409 account_changed responses are the guard working,
    // not bugs. See utils/account_changed_noise.js.
    if (isAccountChangedNoise(notice)) {
      return false;
    }

    // Attribution enrichment. setContext above runs only once at boot and only
    // when window._tkcCurrentUserId is already present, so boundary-caught
    // render errors (and any notice fired before that global lands) arrived
    // with empty context and no way to tell which user/company/flow crashed
    // (HB #133976783 sat unattributed for months). beforeNotify runs for EVERY
    // notice, including the @honeybadger-io/react error-boundary ones, so read
    // the globals FRESH here and merge them in. Existing notice.context wins on
    // any key collision, so per-call context (e.g. the Inertia resolve
    // metadata) is never clobbered. Wrapped so enrichment can NEVER throw and
    // break error reporting.
    try {
      const attribution = {};
      if (window._tkcCurrentUserId != null) {
        attribution.user_id = window._tkcCurrentUserId;
      }
      if (window.bloc_user_email != null) {
        attribution.user_email = window.bloc_user_email;
      }
      if (window._tkcCompanyId != null) {
        attribution.company_id = window._tkcCompanyId;
      }
      if (window._tkcPrimaryCoachId != null) {
        attribution.coach_id = window._tkcPrimaryCoachId;
      }
      // Which shell (web, MobiLoud, Capacitor), platform and native build the
      // notice came from, so app errors can be split from browser errors and
      // tied to a specific App Store / Play build.
      const shellContext = appShellErrorContext();
      notice.context = {
        ...attribution,
        ...shellContext,
        ...(notice.context || {}),
      };
      const extraTags = appShellErrorTags(shellContext);

      // Flow breadcrumb: which page was on screen when it crashed. Kept as both
      // a context field and a tag so triage can filter by path in the HB UI.
      const path = window.location?.pathname;
      if (path) {
        notice.context.current_path = path;
        extraTags.push(`path:${path}`);
      }

      const joined = extraTags.join(", ");
      if (Array.isArray(notice.tags)) {
        notice.tags = [...notice.tags, ...extraTags];
      } else if (typeof notice.tags === "string" && notice.tags.length > 0) {
        notice.tags = `${notice.tags}, ${joined}`;
      } else {
        notice.tags = joined;
      }
    } catch {
      // Best-effort only — a failure here must never suppress the notice.
    }
  });

  const el = document.querySelector("div#app");

  if (el) {
    createInertiaApp({
      resolve: async (name) => {
        // One-shot reload guard per URL per tab (see missing-name branch).
        // Constructed up front so the SUCCESS path can clear it: after a
        // successful recovery the marker must not linger, or a later
        // intermittent glitch on this URL would skip self-heal and hard-fail
        // (and a stray window.name suffix could confuse tab-id consumers).
        const guardKey = `inertia-resolve-reloaded:${window.location.href}`;
        const nameMark = `__tkc_inertia_reloaded__:${window.location.href};`;
        const reloadGuard = {
          isSet() {
            let inStorage = false;
            try {
              inStorage = window.sessionStorage.getItem(guardKey) !== null;
            } catch {
              // unreadable — rely on window.name below
            }
            const inName =
              typeof window.name === "string" && window.name.includes(nameMark);
            return inStorage || inName;
          },
          set() {
            try {
              window.sessionStorage.setItem(guardKey, "1");
            } catch {
              // unwritable — window.name below is the durable marker
            }
            if (
              typeof window.name !== "string" ||
              !window.name.includes(nameMark)
            ) {
              window.name = `${
                typeof window.name === "string" ? window.name : ""
              }${nameMark}`;
            }
          },
          clear() {
            try {
              window.sessionStorage.removeItem(guardKey);
            } catch {
              // nothing to do — storage marker unreachable anyway
            }
            if (typeof window.name === "string") {
              window.name = window.name.split(nameMark).join("");
            }
          },
        };

        if (!name) {
          // #4589 (follow-on to #4499): a visit arrived whose page payload has
          // no component name — malformed/non-Inertia response (stale bundle,
          // auth redirect swallowed into a visit, or edge-cached shell).
          // Throwing here surfaced as an unhandled rejection for the user 28+
          // times in HB. Self-heal instead: a full reload always produces a
          // server-rendered page with a valid data-page.
          //
          // Structured, non-sensitive context only — no raw payload snippets
          // (props can carry customer data; HB is third-party telemetry).
          const raw = document.querySelector("div#app")?.dataset?.page;
          let parsed = null;
          try {
            parsed = raw ? JSON.parse(raw) : null;
          } catch {
            // raw exists but is not JSON — dataPageParses:false says enough.
          }
          const context = {
            href: window.location.href,
            dataPageLength: raw?.length ?? 0,
            dataPageParses: parsed !== null,
            dataPageComponent: parsed?.component ?? null,
            dataPageUrl: parsed?.url ?? null,
            dataPageVersion: parsed?.version ?? null,
          };

          if (!reloadGuard.isSet()) {
            reloadGuard.set();
            // #5720 (HB #134135510, 168+ occ and growing): this branch is
            // the self-heal working as designed. The one-shot guard above
            // guarantees a single reload per URL per tab, never a loop, and
            // the reload always lands on a fresh server render. Routine
            // post-deploy hits of this (concentrated on entry/landing
            // navigations — email deep links, direct nav to /conversations,
            // community channel links — where a stale cached document or
            // bundle is most likely) were paging as a growing Honeybadger
            // error even though nothing needed fixing. Record it as a
            // breadcrumb (informational) instead: only the "still missing
            // after reload" branch below — an actual failure to self-heal —
            // is a real error worth paging on.
            if (typeof honeybadger?.addBreadcrumb === "function") {
              honeybadger.addBreadcrumb(
                "Inertia resolve: page name missing — hard-reloading to recover",
                { category: "inertia", metadata: context },
              );
            }
            // Cache-busting reload: a missing page name on entry/landing
            // navigations is most often a stale cached document or bundle, so a
            // plain cached reload lands on the same dead payload. Force a fresh
            // fetch (clears Cache Storage + `_cb` param) so recovery actually
            // pulls the post-deploy render.
            cacheBustingReload();
            // Never resolves — the reload takes over before this matters.
            return new Promise(() => {});
          }

          // Second hit for the same URL in this tab: the reload did not fix
          // it. Clear the guard (so a later real navigation may retry) and
          // fail fast — the error boundary beats an infinite refresh loop.
          // This IS a real failure (the self-heal reload did not recover),
          // so it stays a full Honeybadger error notice.
          reloadGuard.clear();
          honeybadger.notify(
            "Inertia resolve: page name still missing after reload — failing fast",
            { context },
          );
          throw new Error(
            "Inertia resolve: page name missing after reload — failing fast to error boundary",
          );
        }
        // Successful resolve: clear any lingering marker so a FUTURE
        // intermittent missing-name on this URL self-heals again instead of
        // failing fast (Mecha-Ben r4).
        reloadGuard.clear();
        const module = await import(`../pages/${name}`);
        return module.default;
      },
      setup({ el, App, props }) {
        createRoot(el).render(
          <HoneybadgerErrorBoundary
            honeybadger={honeybadger}
            ErrorComponent={CustomErrorBoundaryComponent}
          >
            <App {...props} />
          </HoneybadgerErrorBoundary>,
        );
        // Definitive "the app booted" signal for the inline entry-load
        // watchdog in application.html.erb. The watchdog fires a one-time
        // cache-busting reload if the entry pack 404'd (stale webview bundle)
        // and this callback therefore never ran; setting it here tells the
        // watchdog to stand down. DOM inspection is only a backstop.
        window.__tkcAppMounted = true;
        // After the first page mounts: hide the launch splash on real content
        // and sync the native status bar to the theme (no-op off the Capacitor
        // shell). Done here (not on DOMContentLoaded) so the splash hides over
        // painted app content, avoiding a white flash.
        initNativeUi();
      },
    });

    // notifications_v2 push-tap → mark-read sync (PR-N1). Reads
    // ?app_notification_id=N from the URL on full page load AND on every
    // Inertia client-side navigation, fires PATCH mark_as_read, strips the
    // param so a back-nav doesn't re-fire. Half B (push dismiss) ships in
    // a later PR.
    initAppNotificationUrlParamHook();

    // Lock impressions are deduped per PAGE-VIEW, and Inertia replaces the page
    // without a document load — so without this the dedupe set never clears and
    // every page after the first under-counts the same lock. Flushes the
    // outgoing page's batch first; the buffer is keyed by feature, not by page.
    router.on("navigate", () => startLockTelemetryPageView());
  }
});
